import { BpOption } from "@models/ui-components/bp-option";
import {
	BUY_ORDERS_PILL_VALUE,
	SELL_ORDERS_PILL_VALUE,
	TOTAL_ORDERS_PILL_VALUE
} from "@helpers/const/following-options";
import { IOrder, IOrderCount } from "../interfaces/limit-orders.remote.interface";
import { IOrderListFilterType } from "../interfaces/limit-order.view.interface";
import { OrderStatus, OrderType } from "@gql/offer";
import { vmFFModel } from "@modules/limit-orders/components/limit-orders-list/limit-orders.component";
import { Fiat } from "@models";
import { Tradable } from "@models/interfaces/tradable";
import { ILimitOrderDetailVM } from "@modules/limit-orders/components/limit-order-detail/limit-order-detail.facade";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";

export function getPills(
	optionArray: BpOption[],
	limitOrdersSell: boolean,
	limitOrdersBuy: boolean,
	sellBalanceForAsset: boolean,
	totalOrders?: boolean
): BpOption[] {
	if (totalOrders) {
		return optionArray.filter(
			(pill) => pill.value !== SELL_ORDERS_PILL_VALUE && pill.value !== BUY_ORDERS_PILL_VALUE
		);
	}

	const excludeValues = new Set<string>();

	if (!limitOrdersSell || (limitOrdersSell && !sellBalanceForAsset)) {
		excludeValues.add(SELL_ORDERS_PILL_VALUE);
	}
	if (!limitOrdersBuy) {
		excludeValues.add(BUY_ORDERS_PILL_VALUE);
	}
	if (!totalOrders) {
		excludeValues.add(TOTAL_ORDERS_PILL_VALUE);
	}

	return optionArray.filter((pill) => !excludeValues.has(pill.value));
}

export function countsDeserializer(
	counts: IOrderCount,
	isBuyLimitEnabledFF: boolean,
	isSellLimitEnabledFF: boolean
): Partial<BpOption[]> {
	const _filter: BpOption[] = [];

	//for type buy order
	if (isBuyLimitEnabledFF) {
		_filter.push({ label: "buy_orders", value: IOrderListFilterType.Buy, amount: +counts.opened.buy });
	}

	//for type sell orders
	if (isSellLimitEnabledFF) {
		_filter.push({
			label: "sell_orders",
			value: IOrderListFilterType.Sell,
			amount: +counts.opened.sell
		});
	}

	//for executed orders
	if (counts.done.buy + counts.done.sell > 0) {
		_filter.push({
			label: "pill_executed_order",
			value: IOrderListFilterType.Executed,
			amount: (isBuyLimitEnabledFF ? +counts.done.buy : 0) + (isSellLimitEnabledFF ? +counts.done.sell : 0)
		});
	}

	//for type cancelled order
	if (
		((counts.closed.buy > 0 || counts.closed.sell > 0) && isBuyLimitEnabledFF) ||
		(counts.closed.sell > 0 && isSellLimitEnabledFF)
	) {
		_filter.push({
			label: "canceled",
			value: IOrderListFilterType.Cancelled,
			amount: (isBuyLimitEnabledFF ? +counts.closed.buy : 0) + (isSellLimitEnabledFF ? +counts.closed.sell : 0)
		});
	}

	return _filter;
}

export function getOrderStatus(status: string): string {
	switch (status) {
		case OrderStatus.Pending:
		case OrderStatus.InProgress:
			return "Open";
		case OrderStatus.Done:
			return "Executed";
		case OrderStatus.Cancelled:
		case OrderStatus.Failed:
			return "Cancelled";
		default:
			return null;
	}
}
export function remotetoVMDeserializer(
	order: IOrder,
	fiat: Fiat,
	tradable: Tradable,
	hasSellBalance: boolean
): Partial<ILimitOrderDetailVM> {
	const {
		amount,
		price,
		orderId,
		createdAt,
		status,
		fiatId,
		assetId,
		hasError,
		itb,
		estimated,
		failedAt,
		failedReason,
		maxBuy,
		minBuy,
		type,
		buyPower,
		executedAt
	} = order;

	return {
		tradable,
		buyAmount: +amount,
		limitAt: +price,
		maxBuy,
		fiat,
		orderId,
		createdAt,
		status,
		fiatPid: fiatId,
		assetPid: assetId,
		hasError,
		itb: +itb,
		estimated: +estimated,
		failedAt,
		failedReason,
		minBuy,
		type,
		buyPower,
		hasSellBalance,
		executedAt,
		showSpread: showInclSpread(tradable?.getAssetGroupName())
	};
}
export function getOrderType(vmFF: vmFFModel): OrderType {
	return vmFF.isBuyEnabled && vmFF.isSellEnabled
		? null
		: vmFF.isBuyEnabled && !vmFF.isSellEnabled
			? OrderType.Buy
			: !vmFF.isBuyEnabled && vmFF.isSellEnabled && OrderType.Sell;
}

export function showInclSpread(assetGroup: string): boolean {
	if (!assetGroup) {
		return false;
	}
	// If the function return true, we show incl. or excl. spread else we show incl. or excl. fees
	return (
		assetGroup === AssetGroupName.Stock || assetGroup === AssetGroupName.Etf || assetGroup === AssetGroupName.Etc
	);
}
